//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConfigContext, type AppConfigUpdate, type ConfigContextValue
} from '@/contexts/ConfigContext';
import { authService, configService, pluginService } from '@/services';
import { type AppConfig, type ConfigXgtUpdate } from '@/services/apiTypes';
import { appConfigKeys, appConfigQuery } from '@/services/appConfigQueries';
import { configQuery, memoryQuery, xgtKeys } from '@/services/xgtQueries';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAuth } from '@/hooks/useAuth';

interface ConfigProviderProps {
  children: React.ReactNode;
}

// Exposes the config state and methods.  All three resources are backed by the
// TanStack Query cache: appConfig (key in appConfigQueries) auto-fetches on
// mount, since the login page needs it before there is a session; configData
// (keys in xgtQueries) auto-fetches once authed; memoryData is driven by the
// Server Status section's polling observer (useMemoryPolling), so this
// provider only reads it.
const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const handleError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const { activeUser } = useAuth();

  const { data: appConfig = null } = useQuery(appConfigQuery());
  // config/xgt is fetched once at login and never expires on its own: the only
  // always-mounted consumer (the Header expiration banner) reads
  // expiration_date, which changes only with a license change + server restart
  // (→ re-login → a fresh fetch).  The mutable parts are shown only in the
  // Settings modal, which refetches this query on open (see SettingsModal);
  // setConfigXgt invalidates it on save.  So no focus-refetch and an infinite
  // staleTime.
  const { data: configData = null } = useQuery({
    ...configQuery(), enabled: !!activeUser,
    staleTime: Infinity, refetchOnWindowFocus: false,
  });
  // memoryData is read here from the shared query cache, but this observer
  // never fetches — the polling observer in the Server Status section
  // (useMemoryPolling) drives memory fetches, so polling is scoped to that
  // Settings tab being open.
  const { data: memoryData = null } = useQuery({ ...memoryQuery(),
                                                 enabled: false });

  // setConfigXgt is a mutation: PUT the changed keys; the PUT returns the
  // authoritative post-write config, which we write straight into the cache —
  // no follow-up GET.  mutateAsync is stable across renders.
  const { mutateAsync: mutateConfigXgt } = useMutation({
    mutationFn: (newSettings: ConfigXgtUpdate) =>
      handleError('setConfigXgt',
                  () => configService.setConfigXgt(newSettings)),
    onSuccess: (data) => {
      queryClient.setQueryData(xgtKeys.config(), data);
    },
  });
  const setConfigXgt = useCallback(
    async (newSettings: ConfigXgtUpdate): Promise<void> => {
      await mutateConfigXgt(newSettings);
    },
    [mutateConfigXgt]);

  // The login page reads config/app itself -- it has to know which auth
  // types to offer, and to tell a backend that is down from one that
  // answered -- and installs what it read here.
  const setAppConfig = useCallback((update: AppConfigUpdate): void => {
    queryClient.setQueryData<AppConfig>(appConfigKeys.all, previous =>
      typeof update === 'function' ? update(previous ?? null) : update);
  }, [queryClient]);

  // Invalidation rather than a plain refetch: a read already out was issued
  // before the change that prompted this call, so its answer cannot be
  // trusted to carry it. Invalidating cancels that read and starts one that
  // postdates the change, and a burst of writes ends on a single answer.
  const refreshAppConfig = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries(
      { queryKey: appConfigKeys.all }, { throwOnError: true });
  }, [queryClient]);

  const testODBC = useCallback((
    connectionString: string, index: number, modified: boolean
  ) =>
    handleError('testODBC',
                () => configService.testODBC(connectionString, index,
                                             modified)),
    [handleError]);

  const fetchPluginInfo = useCallback((url: string) =>
    handleError(`fetchPluginInfo ${url}`,
                () => pluginService.fetchPluginInfo(url)),
    [handleError]);

  const value = useMemo((): ConfigContextValue => ({
    appConfig, setAppConfig,
    configData,
    memoryData,

    // getAppConfig is a pre-auth call (login page) — exposed without the 401
    // wrapper, which would wrongly redirect/loop during login.
    getAppConfig: authService.getAppConfig,
    refreshAppConfig,
    setConfigXgt,
    testODBC,
    fetchPluginInfo
  }), [appConfig, configData, memoryData, setAppConfig, refreshAppConfig,
       setConfigXgt, testODBC, fetchPluginInfo]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigProvider;
